import React from 'react'
import { Box, Typography, LinearProgress } from '@mui/material'
import { CheckCircle, Cancel } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

interface PasswordStrengthIndicatorProps {
  password: string
}

interface PasswordRequirement {
  labelKey: string
  test: (password: string) => boolean
}

const getRequirements = (_t: (key: string) => string): PasswordRequirement[] => [
  {
    labelKey: 'auth.password.requirements.minLength',
    test: (password) => password.length >= 8,
  },
  {
    labelKey: 'auth.password.requirements.uppercase',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    labelKey: 'auth.password.requirements.lowercase',
    test: (password) => /[a-z]/.test(password),
  },
  {
    labelKey: 'auth.password.requirements.number',
    test: (password) => /\d/.test(password),
  },
]

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
}) => {
  const { t } = useTranslation()
  const requirements = getRequirements(t)
  const metRequirements = requirements.filter((req) => req.test(password))
  const strength = metRequirements.length
  const strengthPercentage = (strength / requirements.length) * 100

  const getStrengthColor = () => {
    if (strength <= 1) return 'error'
    if (strength <= 2) return 'warning'
    if (strength <= 3) return 'info'
    return 'success'
  }

  const getStrengthLabel = () => {
    if (strength <= 1) return t('auth.password.strength.weak')
    if (strength <= 2) return t('auth.password.strength.fair')
    if (strength <= 3) return t('auth.password.strength.good')
    return t('auth.password.strength.strong')
  }

  if (!password) return null

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ mr: 1 }}>
          {t('auth.password.strengthLabel')}:
        </Typography>
        <Typography variant="caption" color={`${getStrengthColor()}.main`}>
          {getStrengthLabel()}
        </Typography>
      </Box>
      
      <LinearProgress
        variant="determinate"
        value={strengthPercentage}
        color={getStrengthColor()}
        sx={{ mb: 1, height: 4, borderRadius: 2 }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {requirements.map((requirement, index) => {
          const isMet = requirement.test(password)
          return (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isMet ? (
                <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
              ) : (
                <Cancel sx={{ fontSize: 16, color: 'error.main' }} />
              )}
              <Typography
                variant="caption"
                color={isMet ? 'success.main' : 'text.secondary'}
              >
                {t(requirement.labelKey)}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}